<template>
  <div class="w-full flex flex-col">
    <div class="m-2 p-2 bg-white rounded shadow flex flex-col" style="height: 100%; max-height: 100%;" v-if="loaded">
      <div class="flex flex-row items-center">
        <p class="p-2 text-lg font-medium text-primary flex-grow">{{ route.currentRoute.value.meta.variant === 'military' ? 'Military ' : '' }}Flight Test Crew Matrix</p>
      </div>
      <div class="flex flex-row flex-wrap items-end gap-4 px-2 py-2 mb-1 border-b border-gray-100">
        <div v-for="group in [
          { key: 'role',     label: 'Role',     buttons: ROLE_BUTTONS },
          { key: 'status',   label: 'Status',   buttons: STATUS_BUTTONS },
          { key: 'aircraft', label: 'Aircraft', buttons: AIRCRAFT_BUTTONS },
          { key: 'cs',       label: 'CS Type',  buttons: appConfig.tenantID === 'doa21' ? CS_BUTTONS_DOA : CS_BUTTONS },
        ]" :key="group.key" class="flex flex-col items-start gap-0.5">
          <span class="text-xs text-gray-400 font-medium px-1">{{ group.label }}</span>
          <div class="flex flex-row rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 shadow-inner">
            <button
              v-for="btn in group.buttons"
              :key="btn.value"
              class="px-3 py-1 text-sm rounded-lg transition-colors"
              :class="filters[group.key] === btn.value ? 'bg-primary text-white shadow' : 'text-gray-600 hover:text-primary'"
              @click="toggleFilter(group.key, btn.value)"
            >{{ btn.label }}</button>
          </div>
        </div>
      </div>
      <DataTable
        :tableSettings="{ ...tableConfig, columnState: tableConfig.columnDefs }"
        :items="people"
        :paginationPageSize="50"
        ref="table"
        @item-selected="routeApplication"
      />
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, reactive } from "vue";
import { useStore } from "vuex";
import { DataTable } from "@comp";
import { useRouter } from "vue-router";
import classFinder from "../../../../../resources/classes/FlightTestCrew/classFinder";
import { bestPermissionForType, CS_TYPES } from "../permissions";
import _matrixTableConfig from "./matrixTableConfig.js";
import _matrixTableConfigDoa from "./matrixTableConfig.doa.js";
import { db } from "../../../db.js";
import { appConfig } from "../../../utils";

const route = useRouter();

const store = useStore();
const table = ref(null);
const loaded = ref(false);

const filters = reactive({ role: null, status: "Active", aircraft: null, cs: null });

function toggleFilter(group, value) {
  filters[group] = filters[group] === value ? null : value;
}

const ROLE_BUTTONS    = [{label:'Pilots', value:'Pilot'}, {label:'LFTE & FTE', value:'LFTE_FTE'}, {label:'Observers', value:'Observer'}];
const STATUS_BUTTONS  = [{label:'Active', value:'Active'}, {label:'Expired', value:'Expired'}];
const AIRCRAFT_BUTTONS= [{label:'Aeroplane', value:'Aeroplane'}, {label:'Helicopter', value:'Helicopter'}];
const CS_BUTTONS      = [{label:'CS-23', value:'CS-23'}, {label:'CS-25', value:'CS-25'}, {label:'CS-27', value:'CS-27'}, {label:'CS-29', value:'CS-29'}];
const CS_BUTTONS_DOA  = [{label:'CS-23', value:'CS-23'}, {label:'CS-25', value:'CS-25'}];
const ROLE_LFTE = "Lead Flight Test Engineer";
const ROLE_FTE = ["Flight Test Engineer", "Flight Engineer (Third crew member)"];
const ROLE_OBSERVER = "Other (Observer , Test Equipment Operator)";
function routeApplication(item) {
  
  
  if (!item.data.ID) return;
  route.push({ path: `/FlightTestCrew/Matrix/${item.data.ID}` });
};
// Ratings-based, hierarchical category tick. A Category 1 rating qualifies for
// Cat 1/2/4, a Category 2 rating for Cat 2/4. Cat 4 is satisfied by any rating.
function categoryTick(category, cat) {
  if (!category || category === "None") return false;
  if (cat === 1) return category === "Category1";
  if (cat === 2) return category === "Category1" || category === "Category2";
  return true; // cat 4
}

function dualTick(item, cat) {
  if (item?.Role !== "Pilot") return { aeroplane: false, helicopter: false };
  return {
    aeroplane: categoryTick(item.AeroplaneCategory, cat),
    helicopter: categoryTick(item.HelicopterCategory, cat),
  };
}

function buildRow(item) {
  const isActive = (new Date()).getTime() < (new Date(item.NextExpiry)).getTime();

  const row = {
    Name: `${item?.raw?.User?.FirstName || ""} ${item?.raw?.User?.LastName || ""}`.trim(),
    CCR: item.CCR,
    FTA: item.FTA,
    Limitations: item.Limitations,
    Status: isActive ? "Active" : "Expired",
    PilotCat1: dualTick(item, 1),
    PilotCat2: dualTick(item, 2),
    PilotCat4: dualTick(item, 4),
    LFTE: item.Role === ROLE_LFTE,
    FTE: ROLE_FTE.includes(item.Role),
    Observer: item.Role === ROLE_OBSERVER,
    ID: item.raw.ID,
    Role: item.Role,
    ShowInMilitary: item.ShowInMilitary,
  };

  return row;
}

const people = computed(() => {
  return Object.values(store.state.FlightTestCrew || {})
    .map(classFinder)
    .filter((i) => i.Approved === true && i.Completed === true)
    .map(buildRow)
    .filter((row) => {
      
      const isPilot = row.Role === "Pilot";

      if (filters.role === 'Pilot'    && !isPilot) return false;
      if (filters.role === 'LFTE_FTE' && !row.LFTE && !row.FTE) return false;
      if (filters.role === 'Observer' && !row.Observer) return false;

      if (filters.status && row.Status !== filters.status) return false;

      if (filters.aircraft && isPilot) {
        const key = filters.aircraft === 'Aeroplane' ? 'aeroplane' : 'helicopter';
        const hasAircraft = row.PilotCat1[key] || row.PilotCat2[key] || row.PilotCat4[key];
        if (!hasAircraft) return false;
      }

      if (filters.cs) {
        const hasCS = filters.cs === 'CS-23'
          ? (row['CS-23'] || row['CS-23P'])
          : row[filters.cs];
        if (!hasCS) return false;
      }

      return true;
    }).filter((row) => {
      if(route?.currentRoute.value.meta.variant === "military") {
       return row.ShowInMilitary;
      } else return true;
    })
});

const tableConfig = computed(() => appConfig.tenantID === "doa21" ? _matrixTableConfigDoa() : _matrixTableConfig());

const getData = async () => {
  await db.watch("FlightTestCrew", store);
};

onMounted(async () => {
  await getData();
  loaded.value = true;
});
</script>
